/**
 * ISO 4217 currency code used for formatting monetary values. No white-label deployment
 * exists today, so this doesn't vary by environment.
 */
export const APP_CURRENCY = 'AUD';
/** BCP 47 locale tag used for Intl.NumberFormat/Intl.DateTimeFormat. */
export const APP_CURRENCY_LOCALE = 'en-AU';
/** Product name shown in page titles and the installed PWA. */
export const APP_NAME = 'myTrellis';
/** Deployment tiers a frontend build can target; `local` is `pnpm dev`. */
export const DEPLOY_ENVS = ['local', 'dev', 'uat', 'prod'];
/** Application domain used for UI hints (e.g. email input placeholder). */
export const APP_DOMAIN = 'mytrellis.io';
/** Verified Resend sending domain for all outbound myTrellis email. */
export const EMAIL_DOMAIN = `mail.${APP_DOMAIN}`;
/** Sender for invoice, quote and reminder emails. */
export const INVOICES_SENDER_EMAIL = `invoices@${EMAIL_DOMAIN}`;
/** Sender for account and system emails. */
export const NOTIFICATIONS_SENDER_EMAIL = `notifications@${EMAIL_DOMAIN}`;
/**
 * Monitored support inbox; reply-to for myTrellis-originated email and the contact in
 * legal copy.
 */
export const SUPPORT_EMAIL = `support@${APP_DOMAIN}`;
/** Push notification subject email */
export const PUSH_EMAIL = `push@${APP_DOMAIN}`;
/** Canonical marketing site. */
export const WWW_URL = `https://www.${APP_DOMAIN}`;
/** Product documentation site. */
export const DOCS_URL = `https://docs.${APP_DOMAIN}`;
/**
 * Version of the onboarding wizard's Terms of Service content currently in force —
 * stamped onto `Organization.acceptedTermsOfServiceVersion` server-side whenever an
 * acceptance is recorded. Bump by hand whenever the content changes; no re-consent flow
 * reads this yet (see the Terms of Service Acceptance Gate spec's non-goals).
 */
export const TERMS_OF_SERVICE_VERSION = '2026-10-18';
export const COUNTRIES = [
    { code: 'AU', label: 'Australia', dialCode: '+61' },
    { code: 'CA', label: 'Canada', dialCode: '+1' },
    { code: 'DE', label: 'Germany', dialCode: '+49' },
    { code: 'FR', label: 'France', dialCode: '+33' },
    { code: 'GB', label: 'United Kingdom', dialCode: '+44' },
    { code: 'IN', label: 'India', dialCode: '+91' },
    { code: 'JP', label: 'Japan', dialCode: '+81' },
    { code: 'NL', label: 'Netherlands', dialCode: '+31' },
    { code: 'NZ', label: 'New Zealand', dialCode: '+64' },
    { code: 'SG', label: 'Singapore', dialCode: '+65' },
    { code: 'US', label: 'United States', dialCode: '+1' },
];
export const COUNTRY_CURRENCY = {
    AU: 'AUD',
    CA: 'CAD',
    DE: 'EUR',
    FR: 'EUR',
    GB: 'GBP',
    IN: 'INR',
    JP: 'JPY',
    NL: 'EUR',
    NZ: 'NZD',
    SG: 'SGD',
    US: 'USD',
};
/**
 * COUNTRIES narrowed to Australia only. Passed to PhoneInput's `countries` prop (and
 * parsePhoneValue/formatPhoneValue's `countries` param) at platform-app's phone call
 * sites to lock the country/dial-code picker to AU for the alpha build, without touching
 * the component or the full COUNTRIES list — reopening other countries later means
 * passing COUNTRIES back in at those call sites.
 */
export const AU_ONLY_COUNTRIES = COUNTRIES.filter((country) => country.code === 'AU');
/**
 * Matches AU mobile (04xx xxx xxx) and landline (0[2378] xxxx xxxx) numbers, with or
 * without the +61 country prefix, tolerant of the spaces/hyphens PhoneInput's stored
 * format and manual entry both use. Also accepts a bare 8-digit local landline number
 * with no area/country code (e.g. "9123 4567"), the shorthand still used for numbers
 * dialled within the same area — this form is allowed after +61/0 too, since
 * PhoneFormField always prepends the +61 dial code and users often type the local number
 * without its area code. Tolerates a trunk 0 kept after +61 (people type it from habit)
 * and a single stray leading digit before it (seen in real data as a duplicated/garbled
 * area code ahead of an otherwise-complete number) rather than rejecting the whole number
 * outright.
 */
export const AU_PHONE_REGEX = /^(?:(?:\+61|0)[\s-]?\d?[\s-]?\(?0?\)?[\s-]?(?:[2-9](?:[\s()-]?\d){7}|[2478](?:[\s()-]?\d){8})|[2-9](?:[\s-]?\d){7})$/;
/**
 * Matches an 11-digit ABN, tolerant of the spaces/dashes users commonly type it with:
 * either every digit may be followed by a single space or dash (covers "12-345-678-901"
 * as well as inconsistent spacing), or the standard 2-3-3-3 grouping with optional single
 * spaces between groups (covers "12 345 678 901"). Does not validate the ABN checksum —
 * format only.
 */
export const ABN_REGEX = /^(?:(?:\d[ -]?){10}\d|\d{2} ?\d{3} ?\d{3} ?\d{3})$/;
export const CONTINENT_ORDER = [
    'Australasia / Pacific',
    'Asia',
    'Europe',
    'Africa',
    'Americas',
    'Atlantic / Other',
];
export const CONTINENT_MAP = {
    Africa: 'Africa',
    America: 'Americas',
    Antarctica: 'Australasia / Pacific',
    Arctic: 'Atlantic / Other',
    Asia: 'Asia',
    Atlantic: 'Atlantic / Other',
    Australia: 'Australasia / Pacific',
    Europe: 'Europe',
    Indian: 'Atlantic / Other',
    Pacific: 'Australasia / Pacific',
};
export const CITY_COUNTRY_HINTS = {
    'Australia/Sydney': 'Australia',
    'Australia/Melbourne': 'Australia',
    'Australia/Brisbane': 'Australia',
    'Australia/Perth': 'Australia',
    'Australia/Adelaide': 'Australia',
    'Australia/Darwin': 'Australia',
    'Australia/Hobart': 'Australia',
    'Australia/Lord_Howe': 'Australia',
    'Pacific/Auckland': 'New Zealand',
    'Pacific/Fiji': 'Fiji',
    'Pacific/Guam': 'Guam',
    'Pacific/Honolulu': 'United States',
    'Pacific/Tahiti': 'French Polynesia',
    'Pacific/Tongatapu': 'Tonga',
    'Pacific/Apia': 'Samoa',
    'Pacific/Noumea': 'New Caledonia',
    'Pacific/Port_Moresby': 'Papua New Guinea',
    'Pacific/Chatham': 'New Zealand',
    'Asia/Tokyo': 'Japan',
    'Asia/Shanghai': 'China',
    'Asia/Singapore': 'Singapore',
    'Asia/Hong_Kong': 'Hong Kong',
    'Asia/Seoul': 'South Korea',
    'Asia/Kolkata': 'India',
    'Asia/Dubai': 'UAE',
    'Asia/Bangkok': 'Thailand',
    'Asia/Jakarta': 'Indonesia',
    'Asia/Kuala_Lumpur': 'Malaysia',
    'Asia/Manila': 'Philippines',
    'Asia/Taipei': 'Taiwan',
    'Asia/Karachi': 'Pakistan',
    'Asia/Dhaka': 'Bangladesh',
    'Asia/Colombo': 'Sri Lanka',
    'Asia/Kathmandu': 'Nepal',
    'Asia/Tashkent': 'Uzbekistan',
    'Asia/Almaty': 'Kazakhstan',
    'Asia/Tehran': 'Iran',
    'Asia/Baghdad': 'Iraq',
    'Asia/Riyadh': 'Saudi Arabia',
    'Asia/Beirut': 'Lebanon',
    'Asia/Amman': 'Jordan',
    'Asia/Jerusalem': 'Israel',
    'Asia/Nicosia': 'Cyprus',
    'Asia/Baku': 'Azerbaijan',
    'Asia/Tbilisi': 'Georgia',
    'Asia/Yerevan': 'Armenia',
    'Europe/London': 'United Kingdom',
    'Europe/Paris': 'France',
    'Europe/Berlin': 'Germany',
    'Europe/Rome': 'Italy',
    'Europe/Madrid': 'Spain',
    'Europe/Amsterdam': 'Netherlands',
    'Europe/Brussels': 'Belgium',
    'Europe/Zurich': 'Switzerland',
    'Europe/Vienna': 'Austria',
    'Europe/Prague': 'Czech Republic',
    'Europe/Warsaw': 'Poland',
    'Europe/Budapest': 'Hungary',
    'Europe/Bucharest': 'Romania',
    'Europe/Sofia': 'Bulgaria',
    'Europe/Athens': 'Greece',
    'Europe/Helsinki': 'Finland',
    'Europe/Stockholm': 'Sweden',
    'Europe/Oslo': 'Norway',
    'Europe/Copenhagen': 'Denmark',
    'Europe/Dublin': 'Ireland',
    'Europe/Lisbon': 'Portugal',
    'Europe/Kyiv': 'Ukraine',
    'Europe/Moscow': 'Russia',
    'Europe/Istanbul': 'Turkey',
    'America/New_York': 'United States',
    'America/Los_Angeles': 'United States',
    'America/Chicago': 'United States',
    'America/Denver': 'United States',
    'America/Phoenix': 'United States',
    'America/Toronto': 'Canada',
    'America/Vancouver': 'Canada',
    'America/Sao_Paulo': 'Brazil',
    'America/Buenos_Aires': 'Argentina',
    'America/Mexico_City': 'Mexico',
    'America/Bogota': 'Colombia',
    'America/Lima': 'Peru',
    'America/Santiago': 'Chile',
    'America/Caracas': 'Venezuela',
    'America/Havana': 'Cuba',
    'America/Jamaica': 'Jamaica',
    'Atlantic/Reykjavik': 'Iceland',
    'Atlantic/Azores': 'Portugal',
};
export const FALLBACK_ZONES = [
    'UTC',
    'Australia/Sydney',
    'Australia/Melbourne',
    'Australia/Brisbane',
    'Australia/Perth',
    'Australia/Adelaide',
    'Pacific/Auckland',
    'Asia/Tokyo',
    'Asia/Singapore',
    'Europe/London',
    'Europe/Paris',
    'America/New_York',
    'America/Los_Angeles',
];
/**
 * Maximum active members plus pending invitations per tenant, unless the tenant has an
 * override.
 */
export const USER_ACCOUNT_LIMIT = 5;
/** Upper bound the console accepts for a per-tenant user limit override. */
export const USER_ACCOUNT_LIMIT_CEILING = 100;
//# sourceMappingURL=constants.js.map